import { t } from '@lingui/macro'
import {
    CreateMultiLegShippingEstimateByFuelResponse,
    EmissionFactorWithGasEmissions,
    GasEmissions,
} from '@lune-climate/lune'
import { Text } from '@lune-fe/lune-ui-lib'
import { Box } from '@mui/material'
import { Big } from 'big.js'
import { FC, useMemo } from 'react'

import { useLuneI18nLocale } from '../../hooks'
import { formatNumbers, getFormattedAmount } from '../../utils/utils'

import EmissionFactorExplanationCard from './EmissionFactorExplanationCard'
import EmissionsBreakdowns from './EmissionsBreakdowns'
import ExplanationChip from './ExplanationChip'
import ExplanationSteps, { ExplanationText } from './ExplanationSteps'

interface MultiLegShippingByFuelEmissionExplanationProps {
    estimate: CreateMultiLegShippingEstimateByFuelResponse
    leg: number // 0-indexed
    enableEmissionFactorLink?: boolean
    pdfStructure?: boolean
    smallTitle?: boolean
}

type EnergyDensity =
    CreateMultiLegShippingEstimateByFuelResponse['legs'][number]['methodologyDetails']['energyDensity']

function calculateEnergyConsuptionMJ(
    fuelConsumedLitres: number,
    energyDensity: EnergyDensity,
): Big {
    // Unnecessary but safer in the future.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (energyDensity.unit !== 'MJ/l') {
        throw new Error(`Unexpected energy intensity unit: ${energyDensity.unit}`)
    }

    return Big(fuelConsumedLitres).mul(Big(energyDensity.amount))
}

export const MultiLegShippingByFuelEmissionExplanation: FC<
    MultiLegShippingByFuelEmissionExplanationProps
> = ({ estimate, leg, enableEmissionFactorLink, pdfStructure, smallTitle = false }) => {
    const locale = useLuneI18nLocale()

    const fuelConsumedLitres = estimate.request.legs[leg].fuelConsumedLitres
    const methodologyDetails = estimate.legs[leg].methodologyDetails
    const energyDensity = methodologyDetails.energyDensity

    const energyConsumptionStep = useMemo((): JSX.Element => {
        const energyConsumption = calculateEnergyConsuptionMJ(fuelConsumedLitres, energyDensity)

        return (
            <>
                <ExplanationText text={t`Calculated a vehicle energy consumption of`} />
                <ExplanationChip text={`${formatNumbers(energyConsumption.toString())} MJ`} />
            </>
        )
    }, [fuelConsumedLitres, energyDensity.amount, energyDensity.unit, locale])

    const energyConsumptionInfo = useMemo((): JSX.Element => {
        return (
            <>
                <Text variant={'caption'} component={'p'} sx={{ color: 'white' }}>
                    {t`Fuel energy density`}:{' '}
                    {`${formatNumbers(energyDensity.amount)} ${energyDensity.unit}`}
                </Text>
            </>
        )
    }, [energyDensity.amount, energyDensity.unit, locale])

    const emissionFactor = estimate.legs[leg].emissionFactor
    const emissionFactorStep = useMemo((): JSX.Element => {
        return (
            <>
                <ExplanationText text={t`Selected the`} />
                <ExplanationChip text={emissionFactor.name} />
                <ExplanationText text={t`Emission Factor`} />
            </>
        )
    }, [emissionFactor, locale])

    const formulaStep = <ExplanationText text={t`Multiplied the Emission Factor by the amount`} />

    const steps: { description?: JSX.Element; info?: JSX.Element }[] = [
        { description: energyConsumptionStep, info: energyConsumptionInfo },
        { description: emissionFactorStep },
        { description: formulaStep },
    ]

    const wtwUnit = getFormattedAmount(estimate.legs[leg].mass.amount).amountUnit

    return (
        <>
            <Box>
                <Box>
                    <Text variant={smallTitle ? 'h6' : 'h5'} sx={{ mb: 4 }}>
                        {t`Methodology`}
                    </Text>
                </Box>
                <ExplanationSteps steps={steps} smallTitle={smallTitle} />
            </Box>
            <EmissionsBreakdowns
                emissions={estimate.legs[leg].mass}
                emissionsUnit={wtwUnit}
                pdfStructure={pdfStructure}
                smallTitle={smallTitle}
            />
            <Box>
                <EmissionFactorExplanationCard
                    emissionFactor={
                        emissionFactor as Omit<EmissionFactorWithGasEmissions, 'gasEmissions'> & {
                            gasEmissions: GasEmissions
                        }
                    }
                    amount={null}
                    label={emissionFactor.name}
                    href={
                        enableEmissionFactorLink
                            ? `/emission-factors/${emissionFactor.id}`
                            : undefined
                    }
                />
            </Box>
        </>
    )
}

export default MultiLegShippingByFuelEmissionExplanation
